import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { SelectProps } from '@material-ui/core/Select';

type Option<T = string> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
};

export type FormSelectProps<T = string> = Omit<
  SelectProps,
  'name' | 'value' | 'onChange' | 'multiple' | 'label' | 'margin'
> & {
  name: string;
  control: Control<any>;
  label?: React.ReactNode;
  options: Option<T>[];
  multiple?: boolean;
  helperText?: React.ReactNode;
  formControlProps?: Omit<React.ComponentProps<typeof FormControl>, 'onChange' | 'component'>;
};

function stringKey(v: unknown) {
  return String(v);
}

const FormSelect = React.forwardRef<HTMLDivElement, FormSelectProps<any>>(
  (
    {
      name,
      control,
      label,
      options,
      multiple = false,
      helperText,
      formControlProps,
      variant = 'outlined',
      fullWidth = true,
      disabled,
      ...selectProps
    },
    ref,
  ) => {
    const labelId = `${name}-label`;
    const selectId = `${name}-select`;

    const valueMap = React.useMemo(() => {
      const m = new Map<string, any>();
      options.forEach((o) => m.set(stringKey(o.value), o.value));
      return m;
    }, [options]);

    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const isDisabled = disabled || field.disabled;

          const normalizedValue = multiple
            ? Array.isArray(field.value)
              ? (field.value as any[]).map(stringKey)
              : []
            : field.value == null
            ? ''
            : stringKey(field.value);

          return (
            <FormControl
              size="small"
              variant={variant}
              fullWidth={fullWidth}
              error={hasError}
              disabled={isDisabled}
              {...formControlProps}
            >
              {label && <InputLabel id={labelId}>{label}</InputLabel>}

              <Select
                {...selectProps}
                ref={ref}
                labelId={label ? labelId : undefined}
                id={selectId}
                value={normalizedValue}
                multiple={multiple}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (multiple) {
                    const arr = (raw as string[] | string) ?? [];
                    const values = Array.isArray(arr) ? arr : [arr];
                    field.onChange(values.map((k) => valueMap.get(k) ?? k));
                  } else {
                    const key = raw as string;
                    field.onChange(valueMap.get(key) ?? key);
                  }
                }}
                onBlur={field.onBlur}
                inputProps={{ name: field.name }}
                label={label}
              >
                {options.map((opt) => (
                  <MenuItem
                    key={stringKey(opt.value)}
                    value={stringKey(opt.value)}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }}
      />
    );
  },
);

export default FormSelect;
